// Pure board logic: where a visit sits in a lane, and what a drop means. Kept apart from React
// so it is tested on its own; the server still decides (a drop only proposes an action).

import {
  addDays,
  dateText,
  daysBetween,
  instantAt,
  sentences,
  localDateOf,
  timeParts,
  timeText,
  windowText as kernelWindow,
} from '@flowology/kernel';
import { RpcError } from '@flowology/client';
import { contrast } from '@flowology/tokens';
import { visitWord } from '@flowology/ui-web/words';
import { minutesText } from './books/timesheet-model.ts';
import type { LicenceGap } from './settings/licences-model.ts';
import { isStale } from './shared/rebase.ts';

export interface BoardVisit {
  appointmentId: string;
  jobId: string;
  jobNumber: string;
  jobStatus: string;
  summary: string;
  /** The summary's own words, as the job page reads them (the job summary, step 1); absent before it. */
  scope?: string;
  /** The summary's lines marked for dispatch (bold or between stars), the stars taken off; absent before it. */
  dispatchNotes?: string[];
  /** The buzzer: the imported record's, else the address's codes; only to those who see them. */
  buzzer?: string | null;
  /** The imported record's priority and priority level, in words; null on Flowology's own jobs. */
  priority?: string | null;
  customerName: string;
  address: string;
  /** Where the address is (ServiceTitan's point), for Schedule's map; null with none, absent before it. */
  geo?: { lat: number; lng: number } | null;
  status: 'scheduled' | 'dispatched' | 'working' | 'paused' | 'closed' | 'cancelled' | string;
  start: string;
  end: string;
  arrivalWindow: { start: string; end: string } | null;
  /** Its technicians, the lead first. */
  techIds: string[];
  /** The lead technician, as the server keeps it (never assume techIds[0]). */
  leadTechId: string | null;
  /** Each technician's own state on it (a helper can still be on the way while the lead is on site). */
  techs?: { id: string; fieldStatus: string; pauseReason?: PauseReason | null }[];
  /**
   * Sent, not started: nobody it's sent to has set off yet: no "Start driving" tap (tech_start), or not
   * on the clock yet where the office's dispatch sets them off (QA-687).
   */
  notStarted?: boolean;
  /** When the first tech arrived; running long counts from it (BL-062). */
  arrivedAt?: string | null;
  rowVersion: number;
  /** The address's site contact (F-54): who gets "on the way", when not the customer. */
  siteContactName?: string | null;
  /** BL-027: the licences nobody on this visit holds, valid on its day. */
  licenceGaps?: LicenceGap[];
  /** BL-027: the licences its job type needs, for checking a move before it's made (QA-691); absent before it. */
  licenceNeeds?: { kindId: string; kindName: string }[];
  /** The job's and customer's high-importance tags, by code or name (dispatch parity 3); a cached board may have none. */
  tags?: BoardTag[];
  /** Why and since when it's paused; absent on a board from before them (dispatch parity 4). */
  pauseReason?: PauseReason | null;
  pausedAt?: string | null;
  /** QA-833: the note the technician left as they paused it, and who left it (dispatch.board). */
  pauseNote?: { body: string; staffId: string | null } | null;
  /** The job's business unit and job type, for the filters (dispatch parity 7); absent before them. */
  businessUnit?: { id: string; name: string } | null;
  jobType?: { id: string; name: string } | null;
  /** BL-028: the techs' messages on its job the office hasn't opened; absent before them. */
  newFromTechs?: number;
  /**
   * While mirroring or still bringing ServiceTitan over, a visit from it: when ServiceTitan was last
   * read. Its status is as of then, so it's judged late as of then, not now (history fix 1, 4). Null
   * or absent otherwise.
   */
  stReadAt?: string | null;
}

export interface BoardTag {
  id: string;
  label: string;
  color: string | null;
}

/**
 * The ink for a tag chip on the tag's own colour: black or white, whichever reads better. One of the
 * two always clears 4.5:1 (the worst colour still gives 4.58), so any colour the office picks is legible.
 */
export function tagInk(color: string): '#000000' | '#FFFFFF' {
  return contrast(color, '#000000') >= contrast(color, '#FFFFFF') ? '#000000' : '#FFFFFF';
}

/** The tags in a pill's label: ", tags AM, HMC", or nothing. */
export function tagsLabel(tags: readonly BoardTag[] | undefined): string {
  return tags?.length ? `, tags ${tags.map((t) => t.label).join(', ')}` : '';
}

/**
 * QA-830: a visit its technicians handed to the office with the work done ("Done: hand to the office")
 * stays paused with work_done until the office reviews and completes it. On the board it's done,
 * waiting for the office: its tile, the day's counts and the Done filter, apart from a visit paused
 * with work left.
 */
export const workDone = (v: Pick<BoardVisit, 'status' | 'pauseReason'>): boolean =>
  v.status === 'paused' && v.pauseReason === 'work_done';
/** The state the board shows a visit in: done (closed) for one handed in with the work done (QA-830). */
export const shownStatus = (v: Pick<BoardVisit, 'status' | 'pauseReason'>): string =>
  workDone(v) ? 'closed' : v.status;
/** What a visit handed in with the work done says (QA-830). */
export const WORK_DONE_WORDS = 'Done, waiting for the office';

/** A visit's state in words: "On the way", or "Sent, not started" until the tech sets off. */
export function visitStatusLabel(
  v: Pick<BoardVisit, 'status' | 'notStarted'> & { pauseReason?: PauseReason | null },
  labels: Record<string, string>,
): string {
  if (v.status === 'dispatched' && v.notStarted) return visitWord(v.status, { notStarted: true });
  // a lane's own state can read closed while the visit's still says why it's paused (laneStatus)
  if (v.pauseReason === 'work_done' && (v.status === 'paused' || v.status === 'closed'))
    return WORK_DONE_WORDS;
  return labels[v.status] ?? v.status;
}

export interface BoardTech {
  id: string;
  name: string;
  initials: string;
  color: string | null;
  activeAppointmentId: string | null;
  /** The visit queued to go out when this tech is freed (BL-070); absent from servers before it. */
  nextAppointmentId?: string | null;
  visits: BoardVisit[];
  /** Each day of the board's range, its hours and blocks (dispatch parity 6); absent before it. */
  days?: TechDay[];
  /** One day's board: when they clocked in and out that day (BL-126); absent for a range or before it. */
  clock?: { in: string | null; out: string | null };
  /** BL-027: the licences they hold, kind and expiry (QA-691's check of a move); absent before it. */
  licences?: { kindId: string; expiresOn: string | null }[];
  /** BL-125: where they usually work and where they are this day (divisions); absent before them. */
  homeDivisionId?: string | null;
  divisionId?: string | null;
  /** BL-125: this day's move to another division; one day's board only. */
  divisionMove?: {
    id: string;
    divisionId: string;
    reason: string | null;
    by: string | null;
    at: string;
  } | null;
  /**
   * Their truck in one line, for the tech menu: items below their minimum, stock requests open, and
   * this day's picklist. Null without a truck; absent for a range or from a server before it.
   */
  truck?: {
    locationId: string;
    name: string;
    belowMin: number;
    openRequests: number;
    picklist: 'ready' | 'notBuilt' | 'notLoaded' | null;
  } | null;
}

export interface TechDay {
  date: string;
  workingHours: { start: string; end: string }[];
  blocks: TechBlock[];
}

/** BL-014: what takes a technician away, clipped to the day: time off, a holiday, non-job time. */
export interface TechBlock {
  kind: 'time_off' | 'holiday' | 'non_job';
  /** "Vacation", "Thanksgiving", "Lunch" */
  label: string;
  allDay: boolean;
  start: string;
  /** null while it's still going (non-job time the tech hasn't ended) */
  end: string | null;
  live: boolean;
  /** Paid time (supply house, shop) or not (lunch): the clock line says "On a break" for unpaid (BL-126). */
  paid?: boolean;
}

export interface Board {
  date: string;
  /** The range's last day (the week view); the date itself for one day. */
  to?: string;
  timeZone: string;
  /** dispatch.lateAfterMinutes (BL-062); a board kept from before it was sent has none. */
  lateAfterMinutes?: number;
  /** dispatch.slotMinutes (BL-124): what a dragged visit snaps to; a board kept from before it has none. */
  slotMinutes?: number;
  /** dispatch.licenceRule (BL-027): a move leaving a licence unheld warns or is refused (QA-691); absent before it. */
  licenceRule?: 'warn' | 'refuse';
  /** BL-125: the company's divisions in order; none, or absent, draws the lanes ungrouped. */
  divisions?: { id: string; name: string; unitName?: string }[];
  /** dispatch.openGrouped: the board opens grouped by division for everyone, unless they chose not to. */
  openGrouped?: boolean;
  technicians: BoardTech[];
  unassigned: BoardVisit[];
  /** The visits paused now, whatever their day, the oldest pause first, at most 100 (dispatch parity 4). */
  paused?: BoardVisit[];
  /** How many are paused in all; dispatch.paused lists past the 100. */
  pausedTotal?: number;
  /** While ServiceTitan's visits are on it: when ServiceTitan was last read (history fix 4); null or absent otherwise. */
  serviceTitanAsOf?: string | null;
}

export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 19;

/** Hours since local midnight (fractional) of an instant in a time zone. */
export function localHours(instant: string, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(instant));
  const h = Number(parts.find((p) => p.type === 'hour')!.value);
  const m = Number(parts.find((p) => p.type === 'minute')!.value);
  return h + m / 60;
}

/**
 * Where the board's day ends: 7 p.m., or midnight when the day has an after-hours visit (one that
 * runs past 7 p.m. or across midnight), so a visit booked tonight sits on the board, not past its edge.
 */
export function dayEndHour(visits: Iterable<Pick<BoardVisit, 'start' | 'end'>>, timeZone: string): number {
  for (const v of visits) {
    const s = localHours(v.start, timeZone);
    const e = localHours(v.end, timeZone);
    if (e > DAY_END_HOUR || s >= DAY_END_HOUR || e < s) return 24;
  }
  return DAY_END_HOUR;
}

/** Left offset and width of a visit in a lane, as percentages of the day (DAY_START_HOUR to `endHour`). */
export function placement(
  v: Pick<BoardVisit, 'start' | 'end'>,
  timeZone: string,
  endHour = DAY_END_HOUR,
): { left: number; width: number } {
  const span = endHour - DAY_START_HOUR;
  const clamp = (x: number) => Math.min(endHour, Math.max(DAY_START_HOUR, x));
  const s = clamp(localHours(v.start, timeZone));
  const end = localHours(v.end, timeZone);
  let e = clamp(end < localHours(v.start, timeZone) ? 24 : end); // across midnight: to the day's end
  if (e <= s) e = Math.min(endHour, s + 0.5);
  const round = (x: number) => Math.round(x * 100) / 100;
  return { left: round(((s - DAY_START_HOUR) / span) * 100), width: round(((e - s) / span) * 100) };
}

/** A pill is never drawn narrower than this share of the lane (Board.tsx), so its reach is at least this. */
export const MIN_PILL_PCT = 9;

/**
 * Rows within one lane, so no pill hides another: each visit, in order of where it starts, takes the
 * first row where its pill (as drawn, at least MIN_PILL_PCT wide) touches nothing. One row when
 * nothing overlaps; the lane grows a row for each visit that would otherwise sit on top of another.
 */
export function laneRows(
  visits: readonly Pick<BoardVisit, 'appointmentId' | 'start' | 'end'>[],
  timeZone: string,
  endHour = DAY_END_HOUR,
): { rows: number; rowOf: Map<string, number> } {
  const spans = visits
    .map((v) => {
      const { left, width } = placement(v, timeZone, endHour);
      return { id: v.appointmentId, from: left, to: left + Math.max(width, MIN_PILL_PCT) };
    })
    .sort((a, b) => a.from - b.from || a.to - b.to);
  const ends: number[] = []; // where each row's last pill ends
  const rowOf = new Map<string, number>();
  for (const x of spans) {
    // placement rounds to 0.01 %: a visit that starts as another ends doesn't count as overlapping
    let row = ends.findIndex((end) => end <= x.from + 0.05);
    if (row === -1) row = ends.push(0) - 1;
    ends[row] = x.to;
    rowOf.set(x.id, row);
  }
  return { rows: Math.max(1, ends.length), rowOf };
}

/** The lane's "Out" chip names what the technician is out on: "Out · #24580 Fairview residence". */
export function outChip(tech: Pick<BoardTech, 'activeAppointmentId' | 'visits'>): string | null {
  if (!tech.activeAppointmentId) return null;
  const v = tech.visits.find((x) => x.appointmentId === tech.activeAppointmentId);
  return v ? `Out · #${v.jobNumber} ${v.customerName}` : 'Out';
}

/** "8:00" plus "a.m."/"p.m.", separately, so the time can be large and the suffix small. */
export function clock(instant: string, timeZone: string): { time: string; suffix: string } {
  return timeParts(instant, timeZone);
}

/** "8–10 a.m." (or "8:30–10:30 a.m."): the arrival window promised, or the visit's own times (QA-393). */
export function windowText(v: Pick<BoardVisit, 'start' | 'end' | 'arrivalWindow'>, timeZone: string): string {
  return kernelWindow(v.arrivalWindow?.start ?? v.start, v.arrivalWindow?.end ?? v.end, timeZone);
}

/**
 * The day a visit after today goes out, as the panel says it ("Friday", or "Oct 23" a week or more
 * away), or null for today's: a later visit is dispatched on its day, not ahead of it (QA-325).
 */
export function laterDay(v: Pick<BoardVisit, 'start'>, timeZone: string, today: string): string | null {
  const day = localDateOf(v.start, timeZone);
  if (day <= today) return null;
  return daysBetween(today, day) < 7
    ? new Intl.DateTimeFormat('en-CA', { weekday: 'long', timeZone: 'UTC' }).format(
        new Date(`${day}T12:00:00Z`),
      )
    : dateText(day, 'UTC', { year: false });
}

export type DropTarget = { kind: 'tech'; techId: string } | { kind: 'tray' };
export type DropResult =
  | {
      kind: 'assign';
      action: 'appointments.assign';
      input: { appointmentId: string; techIds: string[]; rowVersion: number };
    }
  | { kind: 'refuse'; message: string }
  | { kind: 'none' };

/**
 * What dropping a visit (dragged out of `fromTechId`'s lane, or the tray) onto a target means.
 * Moving between lanes swaps that technician; other technicians on the visit stay.
 */
export function dropResult(v: BoardVisit, fromTechId: string | null, target: DropTarget): DropResult {
  if (['working', 'paused', 'closed', 'cancelled'].includes(v.status))
    return {
      kind: 'refuse',
      message:
        v.status === 'working' || v.status === 'paused'
          ? 'Work has started on this visit; add another visit instead.'
          : `This visit is ${v.status}.`,
    };
  if (target.kind === 'tray') {
    if (!v.techIds.length) return { kind: 'none' };
    if (v.status === 'dispatched')
      return { kind: 'refuse', message: 'Take the visit back from the technician first.' };
    const techIds = fromTechId ? v.techIds.filter((t) => t !== fromTechId) : [];
    return {
      kind: 'assign',
      action: 'appointments.assign',
      input: { appointmentId: v.appointmentId, techIds, rowVersion: v.rowVersion },
    };
  }
  if (v.techIds.includes(target.techId)) return { kind: 'none' };
  const kept = fromTechId ? v.techIds.filter((t) => t !== fromTechId) : v.techIds;
  return {
    kind: 'assign',
    action: 'appointments.assign',
    input: {
      appointmentId: v.appointmentId,
      techIds: [...kept, target.techId].slice(0, 6),
      rowVersion: v.rowVersion,
    },
  };
}

/**
 * Tap-to-assign: a waiting visit picked in the tray, then a lane tapped (or Enter on it). It means the
 * same as dropping that visit on the lane, so keyboard and touch users get what dragging gives.
 */
export function placeResult(v: BoardVisit, techId: string): DropResult {
  return dropResult(v, null, { kind: 'tech', techId });
}

/**
 * Where a visit is drawn (QA-844): from when work began once a technician has arrived (on site or
 * paused), its booked length on, so the lane's free time and a move's refusals match the picture;
 * otherwise its booked times.
 */
export function shownSpan(v: Pick<BoardVisit, 'status' | 'start' | 'end' | 'arrivedAt'>): {
  start: string;
  end: string;
} {
  if ((v.status !== 'working' && v.status !== 'paused') || !v.arrivedAt)
    return { start: v.start, end: v.end };
  const len = Date.parse(v.end) - Date.parse(v.start);
  const at = Date.parse(v.arrivedAt);
  return { start: new Date(at).toISOString(), end: new Date(at + len).toISOString() };
}

/**
 * What changed on the board between two reads of the same day, in words, for one polite
 * announcement (QA-271). Visits in `skip` (the person's own actions, which already say so) are left
 * out. Nothing when the day differs.
 */
export function boardChanges(
  prev: Board,
  next: Board,
  timeZone: string,
  status: (s: string) => string,
  skip: ReadonlySet<string> = new Set(),
): string[] {
  if (prev.date !== next.date) return [];
  const all = (b: Board) => {
    const m = new Map<string, BoardVisit>();
    for (const t of b.technicians) for (const v of t.visits) m.set(v.appointmentId, v);
    for (const v of b.unassigned) m.set(v.appointmentId, v);
    return m;
  };
  const before = all(prev);
  const after = all(next);
  const names = (ids: readonly string[]) =>
    ids.map((id) => next.technicians.find((t) => t.id === id)?.name ?? 'a technician').join(' and ');
  const at = (v: BoardVisit) => timeText(v.arrivalWindow?.start ?? v.start, timeZone);
  const out: string[] = [];
  for (const [id, v] of after) {
    if (skip.has(id)) continue;
    const was = before.get(id);
    const who = `${v.customerName}’s visit`;
    if (!was) out.push(`New visit for ${v.customerName}, ${at(v)}`);
    else if ([...was.techIds].sort().join() !== [...v.techIds].sort().join())
      out.push(
        v.techIds.length
          ? `${who} assigned to ${names(v.techIds)}, ${at(v)}`
          : `${who} moved back to Waiting`,
      );
    // its new start, never its window's: 2:00 in a 2–4 window moved to 2:30 is said "2:30" (QA-847)
    else if (was.start !== v.start) out.push(`${who} moved to ${timeText(v.start, timeZone)}`);
    // handed in with the work done: said as done, not paused (QA-830)
    else if (workDone(v) && !workDone(was)) out.push(`${who} updated: ${WORK_DONE_WORDS.toLowerCase()}`);
    else if (was.status !== v.status) out.push(`${who} updated: ${status(v.status).toLowerCase()}`);
  }
  for (const [id, v] of before)
    if (!after.has(id) && !skip.has(id)) out.push(`${v.customerName}’s visit left the board`);
  return out;
}

/**
 * What someone else changed on the open visit since the last read, said in its panel (QA-847): the
 * panel no longer changes under you without a word. Null when it didn't change, or the change was the
 * person's own (`skip`).
 */
export function othersChangeTo(
  id: string,
  prev: Board,
  next: Board,
  timeZone: string,
  status: (s: string) => string,
  skip: ReadonlySet<string> = new Set(),
): string | null {
  if (skip.has(id)) return null;
  const only = new Set(
    [
      ...prev.technicians.flatMap((t) => t.visits),
      ...prev.unassigned,
      ...next.technicians.flatMap((t) => t.visits),
      ...next.unassigned,
    ]
      .map((v) => v.appointmentId)
      .filter((x) => x !== id),
  );
  const [said] = boardChanges(prev, next, timeZone, status, new Set([...skip, ...only]));
  return said ? `Someone else changed this visit: ${said}` : null;
}

/**
 * The assign input for a lead and helpers. It sends the visit's rowVersion, so an edit made from a
 * stale board is refused rather than overwriting a newer one (F-53, QA-258).
 */
export function assignInput(v: BoardVisit, lead: string | null, helpers: readonly string[]) {
  const techIds = [...new Set([...(lead ? [lead] : []), ...helpers])].slice(0, 6);
  return {
    appointmentId: v.appointmentId,
    techIds,
    ...(lead ? { leadTechId: lead } : {}),
    rowVersion: v.rowVersion,
  };
}

/** The other visits a technician already has that overlap this one (QA-256): Book warns, so does the board. */
export function overlapsFor(board: Board, techId: string, v: BoardVisit): BoardVisit[] {
  const tech = board.technicians.find((t) => t.id === techId);
  return (tech?.visits ?? []).filter(
    (o) => o.appointmentId !== v.appointmentId && o.start < v.end && v.start < o.end,
  );
}

/** The visit's technicians who are out on another visit now (one active visit each), with that job. */
export function busyTechs(
  board: Board,
  v: BoardVisit,
): { staffId: string; name: string; jobNumber: string | null }[] {
  return v.techIds.flatMap((id) => {
    const t = board.technicians.find((x) => x.id === id);
    if (!t?.activeAppointmentId || t.activeAppointmentId === v.appointmentId) return [];
    const job = t.visits.find((o) => o.appointmentId === t.activeAppointmentId)?.jobNumber ?? null;
    return [{ staffId: t.id, name: t.name, jobNumber: job }];
  });
}

/**
 * Said before Dispatch is pressed: a technician on the visit is out on another one, so this one can
 * go out next instead (BL-070: one active visit per tech, plus one queued next), or null.
 */
export function dispatchBlocker(board: Board, v: BoardVisit): string | null {
  const [b] = busyTechs(board, v);
  return b ? `${b.name} is on ${b.jobNumber ? `#${b.jobNumber}` : 'another visit'}. Send this next?` : null;
}

/** The technician this visit is queued next for (their lane's nextAppointmentId), or null. */
export function queuedFor(
  board: Board,
  v: Pick<BoardVisit, 'appointmentId'>,
): { staffId: string; name: string } | null {
  const t = board.technicians.find((x) => x.nextAppointmentId === v.appointmentId);
  return t ? { staffId: t.id, name: t.name } : null;
}

export const queueInput = (appointmentId: string, staffId: string, replace = false) => ({
  appointmentId,
  staffId,
  ...(replace ? { replace: true } : {}),
});

/** A refused dispatch's busy tech (the server's { busy: [{ staffId, jobNumber }] }), or null. */
export function busyFromRefusal(detail: unknown): { staffId: string; jobNumber: string | null } | null {
  const b = (detail as { busy?: { staffId?: unknown; jobNumber?: unknown }[] } | null)?.busy?.[0];
  return b && typeof b.staffId === 'string'
    ? {
        staffId: b.staffId,
        jobNumber: b.jobNumber === undefined || b.jobNumber === null ? null : String(b.jobNumber),
      }
    : null;
}

/** A refused queueNext: another visit is already next (offer to replace it), the tech is free, or neither. */
export function queueRefusal(detail: unknown): 'queue_full' | 'tech_free' | null {
  const r = (detail as { reason?: unknown } | null)?.reason;
  return r === 'queue_full' || r === 'tech_free' ? r : null;
}

// ── the office records a technician's step for them (F-20) ──

/** Why a visit is paused, in the tech app's words (apps/tech/src/model.ts PAUSE_REASONS). */
export const PAUSE_REASONS = [
  ['work_done', 'Work done', 'The office reviews and completes the job.'],
  ['needs_parts', 'Needs parts', 'The office orders them and books the return.'],
  ['return_visit', 'Return visit', 'More work on another day.'],
  ['customer_unavailable', 'Customer not there', 'The office calls them.'],
  ['end_of_day', 'End of day', 'Back tomorrow on the same visit.'],
] as const;
export type PauseReason = (typeof PAUSE_REASONS)[number][0];

/**
 * The step the office can record for a visit's technicians: they're on the way, so "Arrived for";
 * they're working, so "Pause for", with the reason. Resume is the tech's alone, so a paused visit
 * offers none, and neither does one not yet sent. The server refuses, in words, when it doesn't fit.
 */
export function officeStep(v: Pick<BoardVisit, 'status' | 'techIds'>): 'arrive' | 'pause' | null {
  if (!v.techIds.length) return null;
  return v.status === 'dispatched' ? 'arrive' : v.status === 'working' ? 'pause' : null;
}

export const arriveInput = (appointmentId: string, staffId: string) => ({ appointmentId, staffId });
export const pauseInput = (appointmentId: string, staffId: string, reason: PauseReason) => ({
  appointmentId,
  staffId,
  reason,
});
export const pauseWords = (reason: PauseReason) => PAUSE_REASONS.find(([k]) => k === reason)?.[1] ?? reason;

/** A visit's states in the order the day runs through them (the status bar and the table's sort). */
export const STATUS_ORDER = ['scheduled', 'dispatched', 'working', 'paused', 'closed'] as const;
const statusRank = (s: string) => {
  const i = (STATUS_ORDER as readonly string[]).indexOf(s);
  return i < 0 ? STATUS_ORDER.length : i;
};

/** The day's visits once each, the lanes' and the tray's (a shared visit is in several lanes). */
export function dayVisits(board: Pick<Board, 'technicians' | 'unassigned'>): BoardVisit[] {
  const m = new Map<string, BoardVisit>();
  for (const t of board.technicians) for (const v of t.visits) m.set(v.appointmentId, v);
  for (const v of board.unassigned) m.set(v.appointmentId, v);
  return [...m.values()];
}

/** How many of the day's visits are in each state (dispatch parity 5), in the day's order, none at 0. */
export function statusCounts(visits: readonly BoardVisit[]): { status: string; count: number }[] {
  const n = new Map<string, number>();
  for (const v of visits) n.set(shownStatus(v), (n.get(shownStatus(v)) ?? 0) + 1);
  return [...n.entries()]
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => statusRank(a.status) - statusRank(b.status) || a.status.localeCompare(b.status));
}

export type VisitSortKey = 'time' | 'customer' | 'job' | 'status' | 'techs';
export interface VisitSort {
  key: VisitSortKey;
  dir: 'asc' | 'desc';
}
/** A header's click: the same column turns its order round, another sorts by it from the top. */
export const nextSort = (cur: VisitSort, key: VisitSortKey): VisitSort =>
  cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };

/**
 * The visit table's rows in its order (dispatch parity 5): by time, customer, job number (as numbers),
 * state (the day's order) or technician (the lead's name; nobody yet comes last either way). Ties go
 * by time, so the order never jumps.
 */
export function sortedVisits(
  visits: readonly BoardVisit[],
  sort: VisitSort,
  techName: (id: string) => string,
): BoardVisit[] {
  const text = (a: string, b: string) => a.localeCompare(b, 'en', { sensitivity: 'base', numeric: true });
  const lead = (v: BoardVisit) => {
    const id = v.leadTechId ?? v.techIds[0];
    return id ? techName(id) : null;
  };
  const by = (a: BoardVisit, b: BoardVisit): number => {
    switch (sort.key) {
      case 'customer':
        return text(a.customerName, b.customerName);
      case 'job':
        return text(a.jobNumber, b.jobNumber);
      case 'status':
        return statusRank(shownStatus(a)) - statusRank(shownStatus(b));
      case 'techs':
        return text(lead(a) ?? '', lead(b) ?? '');
      default:
        return 0;
    }
  };
  const sign = sort.dir === 'asc' ? 1 : -1;
  const time = (a: BoardVisit, b: BoardVisit) => Date.parse(a.start) - Date.parse(b.start);
  return [...visits].sort((a, b) => {
    if (sort.key === 'techs' && !lead(a) !== !lead(b)) return lead(a) ? -1 : 1;
    return sign * (by(a, b) || (sort.key === 'time' ? time(a, b) : 0)) || time(a, b);
  });
}

/**
 * The hold list's groups (dispatch parity 4): by reason, in the tech app's order, each keeping the
 * server's order (the oldest pause first); a pause without a reason comes last.
 */
export function pausedGroups(
  paused: readonly BoardVisit[],
): { reason: PauseReason | null; label: string; visits: BoardVisit[] }[] {
  const groups = [
    ...PAUSE_REASONS.map(([reason, label]) => ({ reason: reason as PauseReason | null, label })),
    { reason: null, label: 'No reason given' },
  ];
  return groups
    .map((g) => ({ ...g, visits: paused.filter((v) => (v.pauseReason ?? null) === g.reason) }))
    .filter((g) => g.visits.length > 0);
}

/**
 * Since when a visit is paused, in words: "Since 9:40 a.m." today, "Since yesterday, 4:15 p.m.", or
 * "Since Wed, Oct 14 (4 days)"; from its start when the pause has no time (before paused_at).
 */
export function pausedSince(
  v: Pick<BoardVisit, 'pausedAt' | 'start'>,
  timeZone: string,
  today: string,
): string {
  const at = v.pausedAt ?? v.start;
  const day = localDateOf(at, timeZone);
  const n = daysBetween(day, today);
  if (n <= 0) return `Since ${timeText(at, timeZone)}`;
  if (n === 1) return `Since yesterday, ${timeText(at, timeZone)}`;
  return `Since ${dateText(day, 'UTC', { year: false, weekday: 'short' })} (${n} days)`;
}

/** The warning for technicians added to a visit, or null. */
export function overlapWarning(board: Board, v: BoardVisit, added: readonly string[]): string | null {
  const parts = added.flatMap((t) => {
    const o = overlapsFor(board, t, v);
    const name = board.technicians.find((x) => x.id === t)?.name ?? 'A technician';
    return o.length ? [`${name} already has job #${o.map((x) => x.jobNumber).join(', #')} then`] : [];
  });
  return parts.length ? `Overlap: ${parts.join('; ')}.` : null;
}

/**
 * Events after which the board re-reads itself: its visits, and what the lanes show of the techs'
 * hours and time away (BL-014), and its divisions (arranged, renamed, a move's reason added).
 */
export const BOARD_EVENTS =
  /^((appointment|job|job_message|message|time_off|holiday)\.|time\.(non_job|day)_(started|ended)$|staff\.(hours_set|division_set|division_moved|division_move_undone|division_move_explained)$|business_unit\.updated$)/;

/** BL-028: "1 new message from the techs", "3 new messages from the techs"; null with none. */
export function techMessagesWords(n: number | undefined): string | null {
  return n ? `${n} new ${n === 1 ? 'message' : 'messages'} from the techs` : null;
}

/** dispatch.lateAfterMinutes's own default, for a kept board that doesn't say. */
export const LATE_AFTER_MINUTES = 15;

export interface Overdue {
  kind: 'late' | 'long';
  /** When it was due: the arrival window's close (or the start, with no window), or the planned finish. */
  due: string;
  /** Whole minutes past `due` (as of `asOf`, when it has one). */
  minutes: number;
  /** A mirrored visit's: the last ServiceTitan read, which its status is as of (history fix 1). */
  asOf?: string;
  /**
   * Overdue now, but not as of the last read: nobody knows yet, its status not being read again until
   * the next one. It says "as of the last ServiceTitan read", never late.
   */
  stale?: boolean;
}

/**
 * BL-062. Late: nobody there yet, `after` minutes past the arrival window's close (or the start,
 * with no window). Running long: still working `after` minutes past its planned length, counted
 * from the arrival. Null for anything else, a paused or closed visit among them. Today's board only:
 * the caller doesn't ask for another day.
 */
export function overdue(
  v: Pick<BoardVisit, 'status' | 'start' | 'end' | 'arrivalWindow' | 'arrivedAt' | 'stReadAt'>,
  now: string,
  after = LATE_AFTER_MINUTES,
): Overdue | null {
  const o = overdueAt(v, now, after);
  // a visit ServiceTitan owns is as it was at the last read (history fix 1)
  if (!o || !v.stReadAt || Date.parse(v.stReadAt) >= Date.parse(now)) return o;
  const then = overdueAt(v, v.stReadAt, after);
  return then ? { ...then, asOf: v.stReadAt } : { ...o, asOf: v.stReadAt, stale: true };
}

function overdueAt(
  v: Pick<BoardVisit, 'status' | 'start' | 'end' | 'arrivalWindow' | 'arrivedAt'>,
  now: string,
  after: number,
): Overdue | null {
  let kind: Overdue['kind'];
  let due: number;
  if ((v.status === 'scheduled' || v.status === 'dispatched') && !v.arrivedAt) {
    kind = 'late';
    due = Date.parse(v.arrivalWindow?.end ?? v.start);
  } else if (v.status === 'working') {
    kind = 'long';
    due = Date.parse(v.arrivedAt ?? v.start) + (Date.parse(v.end) - Date.parse(v.start));
  } else return null;
  const past = Math.floor((Date.parse(now) - due) / 60_000);
  if (Number.isNaN(past) || past < after) return null;
  return { kind, due: new Date(due).toISOString(), minutes: past };
}

/** "as of the last read, 9:40 a.m." (source-neutral: the copy never names where the records come from) */
const asOfWords = (asOf: string, timeZone: string) => {
  const t = clock(asOf, timeZone);
  return `as of the last read, ${t.time} ${t.suffix}`;
};

/** "9:40 a.m.", or read on another day than `day`'s, "Oct 21, 11:05 p.m." */
const readAt = (asOf: string, day: string, timeZone: string) =>
  localDateOf(asOf, timeZone) === localDateOf(day, timeZone)
    ? timeText(asOf, timeZone)
    : `${dateText(asOf, timeZone, { year: false, now: day })}, ${timeText(asOf, timeZone)}`;

/**
 * "Imported records as of 9:40 a.m.": the board's line while imported visits are on it (history fix 4),
 * with the day too when the read was another day's. Null otherwise.
 */
export function serviceTitanAsOfText(
  board: Pick<Board, 'date' | 'serviceTitanAsOf'>,
  timeZone: string,
): string | null {
  const t = importReadAt(board, timeZone);
  return t ? `Imported records as of ${t}` : null;
}

/** When the imported records were last read: "9:40 a.m.", or "Oct 21, 11:05 p.m." on another day. */
export function importReadAt(
  board: Pick<Board, 'date' | 'serviceTitanAsOf'>,
  timeZone: string,
): string | null {
  if (!board.serviceTitanAsOf) return null;
  return readAt(board.serviceTitanAsOf, instantAt(board.date, '12:00', timeZone), timeZone);
}

/**
 * The pill's word and the "Needs a look" line: "Late" / "Not there yet · due by 12 p.m., 25 min ago".
 * A mirrored visit's say they're as of the last ServiceTitan read: "Not there yet as of the last
 * ServiceTitan read, 9:40 a.m. · due by 10 a.m.", never late when it wasn't then (history fix 1): one
 * not read since before it was due is "Not updated since 8:40 a.m." (history fix 4).
 */
export function overdueWords(o: Overdue, timeZone: string): { badge: string; line: string } {
  const t = clock(o.due, timeZone);
  const due = `${o.kind === 'late' ? 'due by' : 'planned to finish by'} ${t.time} ${t.suffix}`;
  const state = o.kind === 'late' ? 'Not there yet' : 'Still working';
  if (o.asOf) {
    const line = `${state} ${asOfWords(o.asOf, timeZone)} · ${due}`;
    // not read since before it was due: never late, only not updated since (history fix 4)
    if (o.stale) {
      const since = `Not updated since ${readAt(o.asOf, o.due, timeZone)}`;
      return { badge: since, line: `${since}, the last read · ${due}` };
    }
    return {
      badge: o.kind === 'late' ? 'Late' : 'Running long',
      line: `${line}, ${minutesText(o.minutes)} past it then`,
    };
  }
  const ago = `${minutesText(o.minutes)} ago`;
  return o.kind === 'late'
    ? { badge: 'Late', line: `${state} · ${due}, ${ago}` }
    : { badge: 'Running long', line: `${state} · ${due}, ${ago}` };
}

/**
 * BL-100: a Needs a look row's reason, short: "late 25 min", "running long 1 h 10 min"; a mirrored
 * visit's as of the last read: "late 40 min as of the last ServiceTitan read, 9:40 a.m." (history fix
 * 1), or "not updated since 8:40 a.m." when it wasn't late then (history fix 4).
 */
export function lookWhy(o: Overdue, timeZone?: string): string {
  if (o.stale && o.asOf && timeZone) return `not updated since ${readAt(o.asOf, o.due, timeZone)}`;
  const why = `${o.kind === 'late' ? 'late' : 'running long'} ${minutesText(o.minutes)}`;
  return o.asOf && timeZone ? `${why} ${asOfWords(o.asOf, timeZone)}` : why;
}

/** The colour an overdue visit takes: late's, running long's, or none while it's only as of the last read. */
export const overdueTone = (o: Overdue): 'late' | 'long' | 'stale' => (o.stale ? 'stale' : o.kind);

/** Today's late and long visits, the longest overdue first; a visit with two techs is listed once (by its id). */
export function needsALook(board: Board, now: string): { visit: BoardVisit; overdue: Overdue }[] {
  const seen = new Map<string, { visit: BoardVisit; overdue: Overdue }>();
  for (const v of [...board.technicians.flatMap((t) => t.visits), ...board.unassigned]) {
    const o = overdue(v, now, board.lateAfterMinutes ?? LATE_AFTER_MINUTES);
    if (o) seen.set(v.appointmentId, { visit: v, overdue: o });
  }
  return [...seen.values()].sort((a, b) => b.overdue.minutes - a.overdue.minutes);
}

/**
 * The state a technician's lane shows for a visit: their own on a shared visit (assigned reads as
 * the visit's "scheduled"), the visit's where the board doesn't know theirs (the tray, an old board).
 */
export function laneStatus(
  v: Pick<BoardVisit, 'status' | 'techs'> & { pauseReason?: PauseReason | null },
  techId: string | null,
): string {
  const t = techId ? v.techs?.find((x) => x.id === techId) : undefined;
  if (!t) return shownStatus({ status: v.status, pauseReason: v.pauseReason ?? null });
  // QA-830: their part handed in with the work done is done on their lane; a board from before techs
  // carried their reason goes by the visit's (work_done only once every tech paused so)
  const reason = t.pauseReason !== undefined ? t.pauseReason : v.pauseReason;
  if (t.fieldStatus === 'paused' && reason === 'work_done') return 'closed';
  return t.fieldStatus === 'assigned' ? 'scheduled' : t.fieldStatus;
}

/** The Monday-to-Sunday week a day is in (the week view, dispatch parity 6). */
export function weekOf(date: string): [string, string] {
  const d = new Date(`${date}T12:00:00Z`);
  const back = (d.getUTCDay() + 6) % 7; // Monday 0 … Sunday 6
  d.setUTCDate(d.getUTCDate() - back);
  const from = d.toISOString().slice(0, 10);
  d.setUTCDate(d.getUTCDate() + 6);
  return [from, d.toISOString().slice(0, 10)];
}

/** The days from one date to another, inclusive. */
export function daysFrom(from: string, to: string): string[] {
  return Array.from({ length: daysBetween(from, to) + 1 }, (_, i) => {
    const d = new Date(`${from}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

/** Booked hours to the half hour: "6h", "1.5h". */
export const bookedHours = (minutes: number) => `${Math.round(minutes / 30) / 2}h`;

export interface WeekCell {
  date: string;
  visits: number;
  minutes: number;
  /** No working hours that day. */
  off: boolean;
  /** An all-day time off or holiday's name ("Vacation"), else null. */
  away: string | null;
  /** Their free working minutes that day (capacity.availability, BL-057), when it's known. */
  openMinutes?: number | null;
}

/** A technician's week: their visits by the day they start, and the days they're off or away. */
export function weekCells(
  visits: readonly Pick<BoardVisit, 'start' | 'end'>[],
  days: readonly string[],
  techDays: readonly TechDay[] | undefined,
  timeZone: string,
  /** Each day's free working minutes for this tech (BL-057 phase 2), by date. */
  open?: ReadonlyMap<string, number>,
): WeekCell[] {
  return days.map((date) => {
    const on = visits.filter((v) => localDateOf(v.start, timeZone) === date);
    const td = techDays?.find((d) => d.date === date);
    const away = td?.blocks.find((b) => b.allDay && b.kind !== 'non_job')?.label ?? null;
    return {
      date,
      visits: on.length,
      minutes: on.reduce((m, v) => m + Math.max(0, Date.parse(v.end) - Date.parse(v.start)) / 60_000, 0),
      off: !!td && td.workingHours.length === 0,
      away,
      openMinutes: open?.get(date) ?? null,
    };
  });
}

/** A week cell in words: "3 visits · 6h", "1 visit · 1.5h", then the time off, "Off" or "No visits". */
export function weekCellText(c: WeekCell): string {
  const booked = c.visits
    ? `${c.visits} ${c.visits === 1 ? 'visit' : 'visits'} · ${bookedHours(c.minutes)}`
    : null;
  const why = c.away ?? (c.off ? 'Off' : null);
  // what's still free that day (BL-057), on a working day only
  const free = !why && c.openMinutes ? `${minutesText(c.openMinutes)} open` : null;
  return [booked, why, free].filter(Boolean).join(' · ') || 'No visits';
}

/**
 * A band in a technician's lane (BL-014), placed as visits are: time outside their working hours
 * (`off`), or what takes them away. Bands sit under the pills; a visit booked over one still shows.
 */
export interface LaneBand {
  kind: 'off' | TechBlock['kind'];
  left: number;
  width: number;
  /** The words in the band: "Lunch", "Vacation", "Off"; none for the hours either side of a working day. */
  label: string | null;
  /** On hover: "Lunch, 12–12:30 p.m.", "Lunch since 12:10 p.m.", "Not working 7–8 a.m." */
  title: string;
}

const OFF_SLIVER_MS = 5 * 60_000;

/**
 * A lane's bands for its day: the time outside the tech's hours (the whole lane, "Off", when they
 * don't work it), then time off, a holiday and non-job time over it; a live one runs to `now`.
 */
export function laneBands(
  day: TechDay | undefined,
  timeZone: string,
  endHour: number,
  now: string,
): LaneBand[] {
  if (!day) return [];
  const from = instantAt(day.date, `${String(DAY_START_HOUR).padStart(2, '0')}:00`, timeZone);
  const to =
    endHour >= 24
      ? instantAt(addDays(day.date, 1), '00:00', timeZone)
      : instantAt(day.date, `${String(endHour).padStart(2, '0')}:00`, timeZone);
  const span = Date.parse(to) - Date.parse(from);
  const round = (x: number) => Math.round(x * 100) / 100;
  const later = (x: string, y: string) => (Date.parse(x) >= Date.parse(y) ? x : y);
  const earlier = (x: string, y: string) => (Date.parse(x) <= Date.parse(y) ? x : y);
  const band = (kind: LaneBand['kind'], start: string, end: string, label: string | null, title: string) => {
    const s = Date.parse(later(start, from));
    const e = Date.parse(earlier(end, to));
    // hours that end at 11:59 p.m. run to midnight: no sliver of "not working" for that minute
    return e - s > (kind === 'off' ? OFF_SLIVER_MS : 0)
      ? [
          {
            kind,
            left: round(((s - Date.parse(from)) / span) * 100),
            width: round(((e - s) / span) * 100),
            label,
            title,
          },
        ]
      : [];
  };
  const out: LaneBand[] = [];
  if (!day.workingHours.length) out.push(...band('off', from, to, 'Off', 'Not working this day'));
  else {
    let edge = from;
    for (const w of [...day.workingHours].sort((x, y) => Date.parse(x.start) - Date.parse(y.start))) {
      out.push(...band('off', edge, w.start, null, `Not working ${kernelWindow(edge, w.start, timeZone)}`));
      edge = later(edge, w.end);
    }
    out.push(...band('off', edge, to, null, `Not working ${kernelWindow(edge, to, timeZone)}`));
  }
  for (const b of day.blocks) {
    if (b.allDay) {
      out.push(...band(b.kind, from, to, b.label, b.label));
      continue;
    }
    out.push(...band(b.kind, b.start, b.end ?? later(now, b.start), b.label, blockText(b, timeZone, now)));
  }
  return out;
}

/** A block in words: "Lunch since 12:10 p.m." while it runs, else "Training, 1–3 p.m.". */
function blockText(b: TechBlock, timeZone: string, now: string): string {
  if (b.live) return `${b.label} since ${timeText(b.start, timeZone)}`;
  const end = b.end ?? (Date.parse(now) > Date.parse(b.start) ? now : b.start);
  return `${b.label}, ${kernelWindow(b.start, end, timeZone)}`;
}

/**
 * What the name beside a lane says of the tech's day (BL-014): `chip`, an all-day time off or
 * holiday ("Vacation"), "Off" on a day they don't work, or, today, the non-job time they're on now
 * ("Lunch since 12:10 p.m."); `more`, the day's other blocks in words, for a screen reader (the
 * bands themselves are drawn only).
 */
export function laneAway(
  day: TechDay | undefined,
  isToday: boolean,
  timeZone: string,
  now: string,
): { chip: string | null; more: string[] } {
  if (!day) return { chip: null, more: [] };
  const away = day.blocks.find((b) => b.allDay && b.kind !== 'non_job');
  const live = isToday ? day.blocks.find((b) => b.live) : undefined;
  const chip =
    away?.label ?? (!day.workingHours.length ? 'Off' : live ? blockText(live, timeZone, now) : null);
  const more = day.blocks
    .filter((b) => !b.allDay && !(b === live && chip === blockText(b, timeZone, now)))
    .map((b) => blockText(b, timeZone, now));
  return { chip, more };
}

/** The board's filters (dispatch parity 7): the status bar's state, and a business unit, job type and tag. */
export interface BoardFilter {
  status: string | null;
  unit: string | null;
  type: string | null;
  tag: string | null;
}
export const NO_FILTER: BoardFilter = { status: null, unit: null, type: null, tag: null };

/** Whether a visit is one the filters keep; `but` leaves one of them out (the bar counts by the rest). */
export function keeps(v: BoardVisit, f: BoardFilter, but?: keyof BoardFilter): boolean {
  return (
    (but === 'status' || !f.status || shownStatus(v) === f.status) &&
    (but === 'unit' || !f.unit || v.businessUnit?.id === f.unit) &&
    (but === 'type' || !f.type || v.jobType?.id === f.type) &&
    (but === 'tag' || !f.tag || !!v.tags?.some((t) => t.id === f.tag))
  );
}

/**
 * The Technician choice, and those hidden for the day, applied to the day's list and counts as to the
 * lanes (QA-845): a visit stays while any of its technicians is shown; one with no one yet only while
 * no one technician is picked (the No one yet lane is shown then).
 */
export function keepsTech(
  v: Pick<BoardVisit, 'techIds'>,
  onlyTech: string | null,
  hidden: ReadonlySet<string>,
): boolean {
  if (!v.techIds.length) return !onlyTech;
  return v.techIds.some((id) => (!onlyTech || id === onlyTech) && !hidden.has(id));
}

/**
 * The line over the board while a filter is on (QA-845): what each filter keeps, by name, how many of
 * the day's visits are shown, and what happens to the rest (a technician's lanes are hidden, the other
 * filters grey a visit out where it is). Null with no filter on.
 */
export function filterSummary(
  f: { tech?: string; hidden?: number; unit?: string; type?: string; tag?: string; state?: string },
  shown: number,
  total: number,
): string | null {
  const hides = [
    ...(f.tech ? [`Showing ${f.tech} only`] : []),
    ...(f.hidden ? [`${f.hidden} ${f.hidden === 1 ? 'technician' : 'technicians'} hidden today`] : []),
  ];
  const greys = [
    ...(f.unit ? [`job’s department: ${f.unit}`] : []),
    ...(f.type ? [`job type: ${f.type}`] : []),
    ...(f.tag ? [`tag: ${f.tag}`] : []),
    ...(f.state ? [`state: ${f.state}`] : []),
  ];
  const parts = [...hides, ...greys];
  if (!parts.length) return null;
  const said = parts.join('; ');
  const rest = hides.length && greys.length ? 'hidden or greyed out' : hides.length ? 'hidden' : 'greyed out';
  return `${said.charAt(0).toUpperCase()}${said.slice(1)}: ${shown} of ${total} ${total === 1 ? 'visit' : 'visits'}. The rest are ${rest}.`;
}

/** What the day's visits offer to filter by, each once, by name. */
export function filterOptions(visits: readonly BoardVisit[]): {
  units: { id: string; name: string }[];
  types: { id: string; name: string }[];
  tags: { id: string; name: string }[];
} {
  const once = (xs: ({ id: string; name: string } | null | undefined)[]) =>
    [
      ...new Map(xs.filter((x): x is { id: string; name: string } => !!x).map((x) => [x.id, x])).values(),
    ].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
  return {
    units: once(visits.map((v) => v.businessUnit)),
    types: once(visits.map((v) => v.jobType)),
    tags: once(visits.flatMap((v) => (v.tags ?? []).map((t) => ({ id: t.id, name: t.label })))),
  };
}

/** A visit moved, in words: "Visit moved.", then a start outside its window and who isn't working then. */
export function movedWords(
  r: { outsideWindow?: string | null; hoursConflicts?: { text: string }[] } | null,
): string {
  return sentences('Visit moved', r?.outsideWindow, ...(r?.hoursConflicts ?? []).map((c) => c.text));
}

/**
 * QA-843: the window Move the visit opens on: the one the visit is in now (its arrival window, or its
 * start), so changing only the day keeps the time the customer was given. One that has started on
 * that day gives way to the next that hasn't, then the first that hasn't.
 */
export function moveWindowIndex(
  v: Pick<BoardVisit, 'start' | 'arrivalWindow'>,
  windows: readonly (readonly [number, number])[],
  started: (w: readonly [number, number]) => boolean,
  timeZone: string,
): number {
  const h = localHours(v.arrivalWindow?.start ?? v.start, timeZone);
  const open = (i: number) => i >= 0 && !started(windows[i]!);
  const within = windows.findIndex((w) => w[0] <= h && h < w[1]);
  if (open(within)) return within;
  const later = windows.findIndex((w, i) => w[0] >= h && open(i));
  if (later >= 0) return later;
  const first = windows.findIndex((_, i) => open(i));
  return first < 0 ? 0 : first;
}

/**
 * QA-843: what Move the visit says once it moved, with its Undo: where it went, the day too when it
 * changed ("Moved Gwendolyn to Tue, Nov 3, 4–6 p.m."), and what the server added (outside its window,
 * the technician's hours).
 */
export function movedToLine(
  v: Pick<BoardVisit, 'customerName' | 'start'>,
  to: { start: string; end: string; arrivalWindow?: { start: string; end: string } | null },
  timeZone: string,
  r: { outsideWindow?: string | null; hoursConflicts?: { text: string }[] } | null = null,
): string {
  const day = localDateOf(to.start, timeZone);
  const sameDay = day === localDateOf(v.start, timeZone);
  const when = kernelWindow(to.arrivalWindow?.start ?? to.start, to.arrivalWindow?.end ?? to.end, timeZone);
  const where = sameDay ? when : `${dateText(day, 'UTC', { year: false, weekday: 'short' })}, ${when}`;
  return sentences(
    `Moved ${v.customerName} to ${where}`,
    r?.outsideWindow,
    ...(r?.hoursConflicts ?? []).map((c) => c.text),
  );
}

/**
 * QA-767: a visit someone else changed since the board read it. The board re-reads live, so it already
 * shows their change: no "Reload".
 */
export const VISIT_CHANGED = 'Someone else changed this visit. The board shows their change; try again.';

/**
 * QA-767: what a dialog says once its change is sent while offline (the client sends it on reconnect).
 * Not "Saved.": the offline banner says nothing counts as saved until it says so (QA-849, QA-853's words).
 */
export const OFFLINE_WAITING = 'Not saved yet. It goes when you’re back online, and this closes when it has.';

/** What the board says when a change to a visit is refused: the server's words, but a stale copy's ours. */
export function refusalWords(e: unknown): string {
  if (isStale(e)) return VISIT_CHANGED;
  return e instanceof RpcError ? e.message : 'That did not work. Try again.';
}

/**
 * QA-764: the day a board shows once the company's day has turned from `was` to `now`: one left open
 * on today moves to the new today; one on any other day stays where it was put.
 */
export const rolledDay = (shown: string, was: string, now: string): string =>
  shown === was && now !== was ? now : shown;
